import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Chip } from 'common/components/Chip';
import { InfoRow } from 'common/components/InfoRow';
import { PageDrawer } from 'common/components/PageDrawer';
import { IPageDrawerProps } from 'common/components/PageDrawer/PageDrawer';
import { ISkillRequirementFragment, Maybe } from 'graphql/types';
import { SkillCertificateMatrix } from 'skill/components/SkillCertificateMatrix/SkillCertificateMatrix';

interface IProps extends IPageDrawerProps {
    groupId: string;
    groupName: string;
    groupUserCount?: Maybe<number>;
    skillRequirement?: Maybe<ISkillRequirementFragment>;
    onClose(): void;
}

export const GroupSkillPortfolioItemDrawer = ({
    groupId,
    groupName,
    groupUserCount,
    open,
    skillRequirement,
    onClose,
    ...other
}: IProps) => {
    const [translate] = useTranslation();

    // Cache the requirement so its fields stay stable during the drawer
    // close animation — the parent clears `skillRequirement` on close, which
    // would otherwise drop `skillId` and collapse the drawer content before
    // MUI finishes unmounting.
    const [displayedRequirement, setDisplayedRequirement] =
        useState(skillRequirement);

    if (skillRequirement && skillRequirement !== displayedRequirement) {
        setDisplayedRequirement(skillRequirement);
    }

    const {
        skill,
        compliancy,
        minAbsolute,
        minPercent,
        hasMandatoryCertificates,
    } = displayedRequirement || {};

    const skillId = skill?.id;

    const statusChip = useMemo(() => {
        if (compliancy?.isCompliant === true) {
            return {
                color: 'success' as const,
                label: translate('groupSkillDrawer.statusCompliant'),
            };
        }

        if (compliancy?.isCompliant === false && (minAbsolute || minPercent)) {
            return {
                color: 'error' as const,
                label: translate('groupSkillDrawer.statusNotMet'),
            };
        }

        return null;
    }, [compliancy, minAbsolute, minPercent, translate]);

    const progressLines = useMemo(() => {
        const parts: string[] = [];
        const compliantCount = compliancy?.count ?? 0;

        if (minAbsolute) {
            if (hasMandatoryCertificates) {
                if (compliantCount < minAbsolute) {
                    parts.push(
                        translate('groupSkills.compliancyBelow', {
                            participantAmount: minAbsolute,
                        })
                    );
                } else if (compliantCount > minAbsolute) {
                    parts.push(
                        translate('groupSkills.compliancyAbove', {
                            participantAmount: minAbsolute,
                        })
                    );
                } else {
                    parts.push(
                        translate('groupSkills.compliancyEqual', {
                            count: compliantCount,
                            participantAmount: minAbsolute,
                        })
                    );
                }
            } else {
                parts.push(
                    translate('groupSkills.compliancyProgressCount', {
                        count: compliantCount,
                        participantAmount: minAbsolute,
                    })
                );
            }
        }

        if (minPercent) {
            const percent =
                groupUserCount != null && groupUserCount > 0
                    ? Math.round((compliantCount / groupUserCount) * 100)
                    : 0;

            if (hasMandatoryCertificates) {
                if (percent < minPercent) {
                    parts.push(
                        translate('groupSkills.compliancyPercentBelow', {
                            minPercent,
                        })
                    );
                } else if (percent > minPercent) {
                    parts.push(
                        translate('groupSkills.compliancyPercentAbove', {
                            minPercent,
                        })
                    );
                } else {
                    parts.push(
                        translate('groupSkills.compliancyPercentEqual', {
                            percent,
                        })
                    );
                }
            } else if (percent < minPercent) {
                parts.push(
                    translate('groupSkills.compliancyProgressPercentBelow', {
                        minPercent,
                    })
                );
            } else if (percent > minPercent) {
                parts.push(
                    translate('groupSkills.compliancyProgressPercentAbove', {
                        minPercent,
                    })
                );
            } else {
                parts.push(
                    translate('groupSkills.compliancyProgressPercentEqual', {
                        percent,
                    })
                );
            }
        }

        return parts;
    }, [
        compliancy,
        minAbsolute,
        minPercent,
        groupUserCount,
        hasMandatoryCertificates,
        translate,
    ]);

    const thresholdLines = useMemo(() => {
        const parts: string[] = [];

        if (minAbsolute) {
            parts.push(
                translate('groupSkillDrawer.thresholdAbsolute', {
                    count: minAbsolute,
                    amount: minAbsolute,
                })
            );
        }

        if (minPercent) {
            parts.push(
                translate('groupSkillDrawer.thresholdPercent', {
                    amount: minPercent,
                })
            );
        }

        return parts;
    }, [minAbsolute, minPercent, translate]);

    return (
        <PageDrawer open={open} onClose={onClose} {...other}>
            <Stack spacing={2} sx={{ p: 4 }}>
                <Box>
                    <Stack spacing={-0.5}>
                        <Typography variant="h5">{groupName}</Typography>
                        <Typography variant="h5">{skill?.name}</Typography>
                    </Stack>

                    {statusChip && (
                        <Chip
                            color={statusChip.color}
                            label={statusChip.label}
                            sx={{ mt: 1.5 }}
                        />
                    )}
                </Box>

                <Divider />

                {(!!minAbsolute || !!minPercent) && (
                    <>
                        <Stack spacing={2}>
                            <InfoRow
                                description={translate(
                                    hasMandatoryCertificates
                                        ? 'groupSkillDrawer.compliance'
                                        : 'groupSkillDrawer.progress'
                                )}
                            >
                                {progressLines.length ? (
                                    <Stack spacing={1}>
                                        {progressLines.map((line, index) => (
                                            <Box key={index}>{line}</Box>
                                        ))}
                                    </Stack>
                                ) : (
                                    '-'
                                )}
                            </InfoRow>
                            <InfoRow
                                description={translate(
                                    'groupSkillDrawer.threshold'
                                )}
                            >
                                {thresholdLines.length ? (
                                    <Stack spacing={1}>
                                        {thresholdLines.map((line, index) => (
                                            <Box key={index}>{line}</Box>
                                        ))}
                                    </Stack>
                                ) : (
                                    '-'
                                )}
                            </InfoRow>
                        </Stack>

                        <Divider />
                    </>
                )}

                <SkillCertificateMatrix groupId={groupId} skillId={skillId} />
            </Stack>
        </PageDrawer>
    );
};
